import { useEffect, useRef, useState } from 'react'

import { Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import logo from '~/assets/header_logo.png'

import { useSendMFAEmailCode } from '../hooks/useSendMFAEmailCode'
import { useSendMFAMessageCode } from '../hooks/useSendMFAMessageCode'
import { useVerifyMFAEmailCode } from '../hooks/useVerifyMFAEmailCode'
import { useVerifyMFAMessageCode } from '../hooks/useVerifyMFAMessageCode'

type MFAMethod = 'EMAIL' | 'SMS' | null

export interface MFAScreenProps {
  /** Token from /oauth/token, needed to authenticate MFA requests */
  accessToken: string
  /** User email, if available for MFA delivery */
  email: null | string
  /** User phone number, if available for MFA delivery */
  phoneNumber: null | string
  /** Called when the user successfully verifies the OTP */
  onVerified: () => void
  /** Called when the user cancels the MFA flow — parent should revoke the token */
  onCancel: () => void
}

const CODE_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60

export default function MFAScreen({
  accessToken,
  email,
  phoneNumber,
  onVerified,
  onCancel,
}: MFAScreenProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [step, setStep] = useState<'choose' | 'verify'>('choose')
  const [method, setMethod] = useState<MFAMethod>(null)
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const [rememberMe, setRememberMe] = useState(false)
  const [invalidCode, setInvalidCode] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const cooldownRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  )

  const sendEmail = useSendMFAEmailCode()
  const sendSMS = useSendMFAMessageCode()
  const verifyEmail = useVerifyMFAEmailCode()
  const verifyMessage = useVerifyMFAMessageCode()

  const isSending = sendEmail.isLoading || sendSMS.isLoading
  const isVerifying = verifyEmail.isLoading || verifyMessage.isLoading
  const hasEmailMethod = Boolean(email?.trim())
  const hasSmsMethod = Boolean(phoneNumber?.trim())

  // Clear cooldown timer on unmount
  useEffect(() => {
    return () => {
      clearInterval(cooldownRef.current)
    }
  }, [])

  function startCooldown() {
    clearInterval(cooldownRef.current)
    setCooldown(RESEND_COOLDOWN_SECONDS)
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  function handleSelectMethod(selected: MFAMethod) {
    if (selected === method) return
    setMethod(selected)
    setCooldown(0)
    clearInterval(cooldownRef.current)
    sendEmail.reset()
    sendSMS.reset()
  }

  async function handleSendCode() {
    if (!method) return

    try {
      if (method === 'EMAIL') {
        if (!email?.trim()) return
        await sendEmail.mutateAsync({ email: email.trim(), accessToken })
      } else {
        await sendSMS.mutateAsync({
          phoneNumber: phoneNumber ?? '',
          accessToken,
        })
      }
      setStep('verify')
      setCode(Array(CODE_LENGTH).fill(''))
      setInvalidCode(false)
      startCooldown()
      enqueueSnackbar(t('views.mfa.codeSent'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t('views.mfa.sendError'), { variant: 'error' })
    }
  }

  async function handleResend() {
    if (cooldown > 0 || !method) return
    await handleSendCode()
  }

  function handleCodeChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]

    if (value.length > 1) {
      // Handle paste
      const digits = value.slice(0, CODE_LENGTH).split('')
      digits.forEach((d, i) => {
        if (index + i < CODE_LENGTH) newCode[index + i] = d
      })
      setCode(newCode)
      const nextFocus = Math.min(index + digits.length, CODE_LENGTH - 1)
      inputRefs.current[nextFocus]?.focus()
      return
    }

    newCode[index] = value
    setCode(newCode)

    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  function handleKeyDown(
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const newCode = [...code]
      newCode[index - 1] = ''
      setCode(newCode)
      inputRefs.current[index - 1]?.focus()
    }
  }

  async function handleVerify() {
    const fullCode = code.join('')
    let contactValue: string

    if (method === 'EMAIL') {
      if (!email?.trim()) return
      contactValue = email.trim()
    } else {
      if (!phoneNumber?.trim()) return // TODO: should we use getFormatedNumber when we have the user phoneNumber
      contactValue = phoneNumber.trim()
    }

    if (fullCode.length < CODE_LENGTH) {
      enqueueSnackbar(t('views.mfa.incompleteCode'), { variant: 'warning' })
      return
    }

    try {
      let content: string | undefined

      if (method === 'EMAIL') {
        const response = await verifyEmail.mutateAsync({
          email: contactValue,
          code: fullCode,
          rememberMe,
          accessToken,
        })
        content = response.data.data.verifyMFAEmailCode.content
      } else {
        const response = await verifyMessage.mutateAsync({
          phoneNumber: contactValue,
          code: fullCode,
          rememberMe,
          accessToken,
        })
        content = response.data.data.verifyMFAMessageCode.content
      }

      if (content === 'SUCCESSFUL') {
        onVerified()
      } else {
        // INVALID — let user retry
        setInvalidCode(true)
        setCode(Array(CODE_LENGTH).fill(''))
        inputRefs.current[0]?.focus()
      }
    } catch (error) {
      enqueueSnackbar(t('views.mfa.verifyError'), { variant: 'error' })
    }
  }

  const maskedContact =
    step === 'verify'
      ? method === 'EMAIL'
        ? maskEmail(email ?? '')
        : maskPhone(phoneNumber ?? '')
      : ''

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '100vh',
        bgcolor: theme => theme.palette.background.default,
        p: 2,
      }}
    >
      <Stack
        sx={{
          width: '100%',
          maxWidth: 440,
          bgcolor: theme => theme.palette.background.paper,
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          boxShadow: theme => theme.shadows[4],
        }}
        gap={3}
      >
        {/* Header */}
        <Stack alignItems="center" gap={1.5}>
          <img src={logo} alt="Stoplight Logo" width={56} height={56} />
          <Typography
            variant="h5"
            fontWeight={700}
            textAlign="center"
            sx={{ color: theme => theme.palette.primary.main }}
          >
            {t('views.mfa.title')}
          </Typography>
          <Typography
            variant="body2"
            textAlign="center"
            sx={{ color: theme => theme.palette.text.secondary }}
          >
            {step === 'choose'
              ? t('views.mfa.chooseMethodDescription')
              : method === 'EMAIL'
                ? t('views.mfa.emailSentTo', { email: maskedContact })
                : t('views.mfa.smsSentTo', { phone: maskedContact })}
          </Typography>
        </Stack>

        <Divider />

        {/* Step 1 — Choose method */}
        {step === 'choose' && (
          <Stack gap={2}>
            {/* Method toggle */}
            <Stack direction="row" gap={1}>
              {hasEmailMethod && (
                <MethodButton
                  icon={<EmailIcon />}
                  label={t('views.mfa.email')}
                  selected={method === 'EMAIL'}
                  disabled={isSending}
                  onClick={() => {
                    handleSelectMethod('EMAIL')
                  }}
                />
              )}
              {hasSmsMethod && (
                <MethodButton
                  icon={<PhoneIcon />}
                  label={t('views.mfa.sms')}
                  selected={method === 'SMS'}
                  disabled={isSending}
                  onClick={() => {
                    handleSelectMethod('SMS')
                  }}
                />
              )}
            </Stack>

            {!hasEmailMethod && !hasSmsMethod && (
              <Alert severity="warning">
                {t('views.mfa.noAvailableMethods')}
              </Alert>
            )}

            {method === 'EMAIL' && hasEmailMethod && (
              <Alert severity="info">
                {t('views.mfa.emailWillBeSentTo', {
                  email: maskEmail(email ?? ''),
                })}
              </Alert>
            )}

            {method === 'SMS' && hasSmsMethod && (
              <Alert severity="info">
                {t('views.mfa.smsWillBeSentTo', {
                  phone: maskPhone(phoneNumber ?? ''),
                })}
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={!method || isSending}
              onClick={() => {
                void handleSendCode()
              }}
              startIcon={isSending ? <CircularProgress size={16} /> : null}
            >
              {t('views.mfa.sendCode')}
            </Button>

            {Boolean(sendEmail.error ?? sendSMS.error) && (
              <Alert severity="error">{t('views.mfa.sendError')}</Alert>
            )}
          </Stack>
        )}

        {/* Step 2 — Enter code */}
        {step === 'verify' && (
          <Stack gap={3}>
            {/* OTP inputs */}
            <Stack direction="row" justifyContent="center" gap={1}>
              {code.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={el => {
                    inputRefs.current[index] = el
                  }}
                  value={digit}
                  error={invalidCode}
                  onChange={e => {
                    setInvalidCode(false)
                    handleCodeChange(index, e.target.value)
                  }}
                  onKeyDown={e => {
                    handleKeyDown(
                      index,
                      e as unknown as React.KeyboardEvent<HTMLInputElement>,
                    )
                  }}
                  onPaste={e => {
                    const pasted = e.clipboardData
                      .getData('text')
                      .replace(/\D/g, '')
                    if (!pasted) return
                    e.preventDefault()
                    setInvalidCode(false)
                    handleCodeChange(index, pasted)
                  }}
                  inputProps={{
                    maxLength: CODE_LENGTH,
                    style: {
                      textAlign: 'center',
                      fontSize: 24,
                      fontWeight: 700,
                      padding: '12px 0',
                    },
                  }}
                  sx={{ width: 52 }}
                />
              ))}
            </Stack>

            {invalidCode && (
              <Alert severity="error">{t('views.mfa.invalidCode')}</Alert>
            )}

            {/* Remember me */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={e => {
                    setRememberMe(e.target.checked)
                  }}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {t('views.mfa.rememberMe')}
                </Typography>
              }
            />

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => {
                void handleVerify()
              }}
              disabled={code.join('').length < CODE_LENGTH || isVerifying}
              startIcon={isVerifying ? <CircularProgress size={16} /> : null}
            >
              {t('views.mfa.verifyCode')}
            </Button>

            {/* Resend */}
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Typography
                variant="body2"
                sx={{ color: theme => theme.palette.text.secondary }}
              >
                {t('views.mfa.didntReceive')}
              </Typography>
              <Button
                variant="text"
                size="small"
                disabled={cooldown > 0 || isSending}
                onClick={() => {
                  void handleResend()
                }}
              >
                {cooldown > 0
                  ? t('views.mfa.resendIn', { seconds: cooldown })
                  : t('views.mfa.resend')}
              </Button>
            </Stack>

            {/* Back to method selection */}
            <Box textAlign="center">
              <Button
                variant="text"
                size="small"
                sx={{ color: theme => theme.palette.text.secondary }}
                onClick={() => {
                  setStep('choose')
                  setMethod(null)
                  setCode(Array(CODE_LENGTH).fill(''))
                  setInvalidCode(false)
                  setCooldown(0)
                  clearInterval(cooldownRef.current)
                }}
              >
                {t('views.mfa.changeMethod')}
              </Button>
            </Box>
          </Stack>
        )}

        <Divider />

        {/* Cancel — always visible */}
        <Box textAlign="center">
          <Button
            variant="text"
            size="small"
            sx={{ color: theme => theme.palette.text.disabled }}
            onClick={onCancel}
          >
            {t('views.mfa.cancel')}
          </Button>
        </Box>
      </Stack>
    </Stack>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface MethodButtonProps {
  icon: React.ReactNode
  label: string
  selected: boolean
  disabled: boolean
  onClick: () => void
}

function MethodButton({
  icon,
  label,
  selected,
  disabled,
  onClick,
}: MethodButtonProps) {
  return (
    <Button
      variant={selected ? 'contained' : 'outlined'}
      fullWidth
      disabled={disabled}
      onClick={onClick}
      startIcon={icon}
      sx={{
        textTransform: 'none',
        py: 1.5,
        borderRadius: 2,
      }}
    >
      {label}
    </Button>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!local || !domain) return email
  const visible = local.slice(0, 2)
  return `${visible}${'*'.repeat(Math.max(local.length - 2, 2))}@${domain}`
}

function maskPhone(phone: string): string {
  if (phone.length < 6) return phone
  return `${'*'.repeat(phone.length - 4)}${phone.slice(-4)}`
}
